/**
 * LLM Pool Control Center - Admin UI
 * Routes:
 * - /llm-pool
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AiConfigModal } from '../components/aiconfig';
import {
  hubService,
  type HubLlmPool,
  type HubLlmPoolModel,
  type InferenceGenerateResult,
  type LlmPoolPolicy,
  type LlmPoolUsageCounterRow,
} from '../services/hub.service';
import { isLlmProviderId, LLM_PROVIDERS } from '../constants/llmProviders';

type LlmPoolTab = 'pools' | 'policies' | 'usage' | 'test';

/** Pool test adapter: OpenAI-compatible vs Gemini-native (see hub inference / LLM pool dispatcher). */
type PoolTestAdapter = 'http' | 'langchain' | 'genai-sdk' | 'genai-http';

type PoolFormMode = 'create' | 'edit';

type PoolModelFormRow = {
  _key: string;
  id?: string;
  modelKey: string;
  displayName: string;
  endpointKey: string;
  enabled: boolean;
};

const DEFAULT_SYSTEM = 'chatbot';

function newRowKey(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function emptyPoolModelRow(): PoolModelFormRow {
  return {
    _key: newRowKey(),
    modelKey: '',
    displayName: '',
    endpointKey: '',
    enabled: true,
  };
}

function parseEndpointKeysFromJson(endpointsJson: string): string[] {
  try {
    const o = JSON.parse(endpointsJson) as unknown;
    if (!o || typeof o !== 'object' || Array.isArray(o)) return [];
    return Object.keys(o).filter((k) => typeof (o as Record<string, string>)[k] === 'string');
  } catch {
    return [];
  }
}

function LlmProviderSelect(props: {
  id?: string;
  value: string;
  onChange: (provider: string) => void;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  className?: string;
  /** When current value is not in LLM_PROVIDERS (legacy DB row), keep it selectable. */
  allowUnknownValue?: boolean;
}) {
  const {
    id,
    value,
    onChange,
    disabled,
    allowEmpty,
    emptyLabel = '—',
    className,
    allowUnknownValue,
  } = props;
  const unknown = allowUnknownValue && value && !isLlmProviderId(value);
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={className ?? 'rounded border border-gray-300 px-2 py-1.5'}
    >
      {allowEmpty ? <option value="">{emptyLabel}</option> : null}
      {LLM_PROVIDERS.map((p) => (
        <option key={p} value={p}>
          {p}
        </option>
      ))}
      {unknown ? (
        <option value={value}>
          {value} (custom)
        </option>
      ) : null}
    </select>
  );
}

/** Pick a key from the pool `endpoints` JSON (matches `llm_pool_models.endpoint_key`). */
function PoolEndpointKeySelect(props: {
  endpointKeys: string[];
  value: string;
  onChange: (endpointKey: string) => void;
  disabled?: boolean;
}) {
  const keys = props.endpointKeys;
  const v = props.value ?? '';
  const unknown = v !== '' && !keys.includes(v);

  if (keys.length === 0) {
    return (
      <input
        type="text"
        value={v}
        onChange={(e) => props.onChange(e.target.value)}
        disabled={props.disabled}
        placeholder="default, transcribe, …"
        className="rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
      />
    );
  }

  return (
    <select
      value={v}
      onChange={(e) => props.onChange(e.target.value)}
      disabled={props.disabled}
      className="rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
    >
      <option value="">Default (pool resolver)</option>
      {keys
        .slice()
        .sort((a, b) => a.localeCompare(b))
        .map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      {unknown ? (
        <option value={v}>
          {v} (not in endpoints JSON)
        </option>
      ) : null}
    </select>
  );
}

/** Model keys from enabled pools for the pool-test provider filter (plus `providerConfig.model` when set). */
function poolTestModelsForProvider(pools: HubLlmPool[], providerTrimmed: string): string[] {
  if (!providerTrimmed) return [];
  const pn = providerTrimmed.toLowerCase();
  const keys = new Set<string>();
  for (const p of pools) {
    if (p.provider.trim().toLowerCase() !== pn) continue;
    if (!p.enabled || p.draining) continue;
    const cfg = p.providerConfig?.model;
    if (typeof cfg === 'string' && cfg.trim()) keys.add(cfg.trim());
    for (const m of p.models ?? []) {
      if (m.enabled && m.modelKey.trim()) keys.add(m.modelKey.trim());
    }
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

function PoolTestModelSelect(props: {
  value: string;
  onChange: (modelKey: string) => void;
  disabled?: boolean;
  providerSelected: boolean;
  modelKeys: string[];
}) {
  const v = props.value ?? '';
  const keys = props.modelKeys;
  const unknown = props.providerSelected && v !== '' && !keys.includes(v);

  return (
    <select
      value={v}
      onChange={(e) => props.onChange(e.target.value)}
      disabled={props.disabled || !props.providerSelected}
      className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono min-w-0 w-full max-w-md"
    >
      {!props.providerSelected ? (
        <option value="">Choose a provider first</option>
      ) : (
        <option value="">Any model</option>
      )}
      {keys.map((k) => (
        <option key={k} value={k}>
          {k}
        </option>
      ))}
      {unknown ? (
        <option value={v}>
          {v} (not in list)
        </option>
      ) : null}
    </select>
  );
}

export function LlmPoolControl() {
  const { getToken } = useAuth();
  const [tab, setTab] = useState<LlmPoolTab>('pools');

  const [poolsLoading, setPoolsLoading] = useState(true);
  const [poolsError, setPoolsError] = useState<string | null>(null);
  const [pools, setPools] = useState<HubLlmPool[]>([]);

  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [policies, setPolicies] = useState<LlmPoolPolicy[]>([]);
  const [policyDrafts, setPolicyDrafts] = useState<
    Record<string, { enabled: boolean; strategy: string; fallbackProviders: string; maxRetries: number }>
  >({});

  const [savingPolicyId, setSavingPolicyId] = useState<string | null>(null);

  const [showPoolForm, setShowPoolForm] = useState(false);
  const [poolFormMode, setPoolFormMode] = useState<PoolFormMode>('create');
  const [poolFormPoolId, setPoolFormPoolId] = useState<string | null>(null);
  const [poolFormSaving, setPoolFormSaving] = useState(false);
  const [poolFormOriginalModels, setPoolFormOriginalModels] = useState<HubLlmPoolModel[]>([]);
  const [poolForm, setPoolForm] = useState({
    key: '',
    name: '',
    description: '',
    provider: '',
    apiKey: '',
    endpointsJson: '{"default":"https://api.openai.com/v1"}',
    providerConfigJson: '{}',
    enabled: true,
    draining: false,
    clearApiKey: false,
    modelRows: [] as PoolModelFormRow[],
  });

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [creatingPolicy, setCreatingPolicy] = useState(false);

  const [newPolicy, setNewPolicy] = useState({
    name: '',
    system: '',
    botId: '',
    strategy: 'capability_first',
    fallbackProviders: '',
    maxRetries: '2',
    enabled: true,
  });

  const [usageRealtimeLoading, setUsageRealtimeLoading] = useState(true);
  const [usageRealtimeError, setUsageRealtimeError] = useState<string | null>(null);
  const [usageRealtime, setUsageRealtime] = useState<LlmPoolUsageCounterRow[]>([]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultFrom = useMemo(
    () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    [],
  );

  const [system, setSystem] = useState(DEFAULT_SYSTEM);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(today);
  const [provider, setProvider] = useState('');

  const [usageSummaryLoading, setUsageSummaryLoading] = useState(false);
  const [usageSummaryError, setUsageSummaryError] = useState<string | null>(null);
  const [usageSummary, setUsageSummary] = useState<LlmPoolUsageCounterRow[]>([]);

  const [poolTestSystemPrompt, setPoolTestSystemPrompt] = useState(
    'You are a helpful assistant. Reply briefly.',
  );
  const [poolTestUserPrompt, setPoolTestUserPrompt] = useState('What is 2+2?');
  const [poolTestMode, setPoolTestMode] = useState<
    'text' | 'structured' | 'tool' | 'stream'
  >('text');
  const [poolTestAdapter, setPoolTestAdapter] = useState<PoolTestAdapter>('http');
  /** Empty = use pool row `provider_config.transport`; non-empty sets inference `transport` (hub `policy.transportOverride`). */
  const [poolTestTransport, setPoolTestTransport] = useState('');
  const [poolTestProvider, setPoolTestProvider] = useState('');
  const [poolTestModel, setPoolTestModel] = useState('');
  const [poolTestToolsJson, setPoolTestToolsJson] = useState(
    '[{"name":"get_time","description":"Get current time","inputSchema":{"type":"object","properties":{}}}]',
  );
  const [poolTestToolContextJson, setPoolTestToolContextJson] = useState('{}');
  const [poolTestTemperature, setPoolTestTemperature] = useState('0.7');
  const [poolTestMaxTokens, setPoolTestMaxTokens] = useState('512');
  const [poolTestLoading, setPoolTestLoading] = useState(false);
  const [poolTestError, setPoolTestError] = useState<string | null>(null);
  const [poolTestResult, setPoolTestResult] = useState<InferenceGenerateResult | null>(null);

  const loadPools = useCallback(async () => {
    setPoolsLoading(true);
    setPoolsError(null);
    try {
      const token = getToken();
      const data = await hubService.listLlmPools(token);
      setPools(data);
    } catch (e) {
      setPoolsError(e instanceof Error ? e.message : 'Failed to load LLM pools');
    } finally {
      setPoolsLoading(false);
    }
  }, [getToken]);

  const loadPolicies = useCallback(async () => {
    setPoliciesLoading(true);
    setPoliciesError(null);
    try {
      const token = getToken();
      const data = await hubService.listLlmPoolPolicies(token);
      setPolicies(data);
      setPolicyDrafts(
        Object.fromEntries(
          data.map((p) => [
            p.id,
            {
              enabled: p.enabled,
              strategy: p.strategy,
              fallbackProviders: p.fallbackProviders ?? '',
              maxRetries: p.maxRetries,
            },
          ]),
        ),
      );
    } catch (e) {
      setPoliciesError(e instanceof Error ? e.message : 'Failed to load llm-pool policies');
    } finally {
      setPoliciesLoading(false);
    }
  }, [getToken]);

  const loadUsageRealtime = useCallback(async () => {
    setUsageRealtimeLoading(true);
    setUsageRealtimeError(null);
    try {
      const token = getToken();
      const data = await hubService.getLlmPoolUsageRealtime(token);
      setUsageRealtime(data);
    } catch (e) {
      setUsageRealtimeError(e instanceof Error ? e.message : 'Failed to load llm-pool usage');
    } finally {
      setUsageRealtimeLoading(false);
    }
  }, [getToken]);

  const loadUsageSummary = useCallback(async () => {
    setUsageSummaryLoading(true);
    setUsageSummaryError(null);
    try {
      const token = getToken();
      const data = await hubService.getLlmPoolUsageSummary(
        {
          system: system.trim() || undefined,
          from,
          to,
          provider: provider.trim() || undefined,
        },
        token,
      );
      setUsageSummary(data);
    } catch (e) {
      setUsageSummaryError(e instanceof Error ? e.message : 'Failed to load llm-pool usage summary');
    } finally {
      setUsageSummaryLoading(false);
    }
  }, [getToken, system, from, to, provider]);

  useEffect(() => {
    loadPools();
    loadPolicies();
    loadUsageRealtime();
    loadUsageSummary();
  }, [loadPools, loadPolicies, loadUsageRealtime, loadUsageSummary]);

  const poolTestIsGeminiProvider = poolTestProvider.trim().toLowerCase() === 'gemini';

  useEffect(() => {
    setPoolTestAdapter((prev) => {
      if (poolTestIsGeminiProvider) {
        if (prev === 'http' || prev === 'langchain') return 'genai-sdk';
        return prev;
      }
      if (prev === 'genai-sdk' || prev === 'genai-http') return 'http';
      return prev;
    });
  }, [poolTestIsGeminiProvider]);

  /** Gemini transport override is only `genai-http` or empty; strip groq/http hints when switching provider. */
  useEffect(() => {
    if (poolTestIsGeminiProvider) {
      setPoolTestTransport((t) => {
        const tl = t.trim().toLowerCase();
        if (tl !== '' && tl !== 'genai-http') return '';
        return t;
      });
      return;
    }
    setPoolTestTransport((t) => (t.trim().toLowerCase() === 'genai-http' ? '' : t));
  }, [poolTestIsGeminiProvider]);

  const poolTestGeminiStackMode = useMemo(() => {
    if (!poolTestIsGeminiProvider) return null;
    const tr = poolTestTransport.trim().toLowerCase();
    if (tr === 'genai-http' || poolTestAdapter === 'genai-http') return 'http' as const;
    return 'sdk' as const;
  }, [poolTestIsGeminiProvider, poolTestTransport, poolTestAdapter]);

  const poolTestProviderSelected = poolTestProvider.trim().length > 0;
  const poolTestSelectableModels = useMemo(
    () => poolTestModelsForProvider(pools, poolTestProvider.trim()),
    [pools, poolTestProvider],
  );

  /** Avoid re-forcing the only model after the user picks "Any model" for that provider. */
  const poolTestSingleModelAutoPickedFor = useRef<string | null>(null);

  useEffect(() => {
    const pv = poolTestProvider.trim();
    if (!pv) {
      setPoolTestModel('');
      poolTestSingleModelAutoPickedFor.current = null;
      return;
    }
    const keys = poolTestSelectableModels;
    const pKey = pv.toLowerCase();

    if (keys.length === 1) {
      if (poolTestSingleModelAutoPickedFor.current !== pKey) {
        poolTestSingleModelAutoPickedFor.current = pKey;
        setPoolTestModel(keys[0]);
      }
      return;
    }

    poolTestSingleModelAutoPickedFor.current = null;
    setPoolTestModel((prev) => (prev && keys.includes(prev) ? prev : ''));
  }, [poolTestProvider, poolTestSelectableModels]);

  const savePolicy = useCallback(
    async (
      id: string,
      dto: { enabled?: boolean; strategy?: string; fallbackProviders?: string; maxRetries?: number },
    ) => {
      setSavingPolicyId(id);
      try {
        const token = getToken();
        await hubService.patchLlmPoolPolicy(id, dto, token);
        await loadPolicies();
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Failed to save policy');
      } finally {
        setSavingPolicyId(null);
      }
    },
    [getToken, loadPolicies],
  );

  const closePoolForm = useCallback(() => {
    setShowPoolForm(false);
    setPoolFormPoolId(null);
    setPoolFormOriginalModels([]);
    setPoolForm({
      key: '',
      name: '',
      description: '',
      provider: '',
      apiKey: '',
      endpointsJson: '{"default":"https://api.openai.com/v1"}',
      providerConfigJson: '{}',
      enabled: true,
      draining: false,
      clearApiKey: false,
      modelRows: [],
    });
  }, []);

  const openPoolFormCreate = useCallback(() => {
    setPoolFormMode('create');
    setPoolFormPoolId(null);
    setPoolFormOriginalModels([]);
    setPoolForm({
      key: '',
      name: '',
      description: '',
      provider: '',
      apiKey: '',
      endpointsJson: '{"default":"https://api.openai.com/v1"}',
      providerConfigJson: '{}',
      enabled: true,
      draining: false,
      clearApiKey: false,
      modelRows: [emptyPoolModelRow()],
    });
    setShowPoolForm(true);
  }, []);

  const openPoolFormEdit = useCallback((p: HubLlmPool) => {
    setPoolFormMode('edit');
    setPoolFormPoolId(p.id);
    setPoolFormOriginalModels(p.models ?? []);
    const rows: PoolModelFormRow[] =
      (p.models?.length ?? 0) > 0
        ? p.models!.map((m) => ({
            _key: m.id,
            id: m.id,
            modelKey: m.modelKey,
            displayName: m.displayName ?? '',
            endpointKey: m.endpointKey ?? '',
            enabled: m.enabled,
          }))
        : [emptyPoolModelRow()];
    setPoolForm({
      key: p.key,
      name: p.name,
      description: p.description ?? '',
      provider: p.provider?.trim() ?? '',
      apiKey: '',
      endpointsJson: JSON.stringify(p.endpoints ?? {}, null, 2),
      providerConfigJson: JSON.stringify(p.providerConfig ?? {}, null, 2),
      enabled: p.enabled,
      draining: p.draining,
      clearApiKey: false,
      modelRows: rows,
    });
    setShowPoolForm(true);
  }, []);

  const addPoolModelRow = useCallback(() => {
    setPoolForm((s) => ({ ...s, modelRows: [...s.modelRows, emptyPoolModelRow()] }));
  }, []);

  const removePoolModelRow = useCallback((index: number) => {
    setPoolForm((s) => ({
      ...s,
      modelRows: s.modelRows.filter((_, i) => i !== index),
    }));
  }, []);

  const resetNewPolicy = useCallback(() => {
    setNewPolicy({
      name: '',
      system: '',
      botId: '',
      strategy: 'capability_first',
      fallbackProviders: '',
      maxRetries: '2',
      enabled: true,
    });
  }, []);

  const handleSavePoolForm = useCallback(async () => {
    if (!poolForm.key.trim() || !poolForm.name.trim() || !poolForm.provider) {
      alert('Pool key, name, and provider are required.');
      return;
    }
    let endpoints: Record<string, string>;
    let providerConfig: Record<string, unknown>;
    try {
      endpoints = JSON.parse(poolForm.endpointsJson) as Record<string, string>;
      if (!endpoints || typeof endpoints !== 'object' || Array.isArray(endpoints)) {
        throw new Error('endpoints must be a JSON object');
      }
    } catch {
      alert('Endpoints must be valid JSON object, e.g. {"default":"https://host/v1"}');
      return;
    }
    try {
      providerConfig = JSON.parse(poolForm.providerConfigJson) as Record<string, unknown>;
      if (!providerConfig || typeof providerConfig !== 'object' || Array.isArray(providerConfig)) {
        throw new Error('providerConfig must be a JSON object');
      }
    } catch {
      alert('Provider config must be valid JSON object.');
      return;
    }

    const drafts = poolForm.modelRows.filter((r) => r.modelKey.trim());

    setPoolFormSaving(true);
    try {
      const token = getToken();
      if (poolFormMode === 'create') {
        const models =
          drafts.length > 0
            ? drafts.map((row) => ({
                modelKey: row.modelKey.trim(),
                displayName: row.displayName.trim() || null,
                endpointKey: row.endpointKey.trim() || null,
                enabled: row.enabled,
              }))
            : undefined;
        await hubService.createLlmPool(
          {
            key: poolForm.key.trim(),
            name: poolForm.name.trim(),
            description: poolForm.description.trim() || null,
            provider: poolForm.provider,
            apiKey: poolForm.apiKey.trim() || null,
            endpoints,
            providerConfig,
            enabled: poolForm.enabled,
            draining: poolForm.draining,
            models,
          },
          token,
        );
      } else {
        const poolId = poolFormPoolId;
        if (!poolId) return;
        const patchDto: Parameters<typeof hubService.patchLlmPool>[1] = {
          key: poolForm.key.trim(),
          name: poolForm.name.trim(),
          description: poolForm.description.trim() ? poolForm.description.trim() : null,
          provider: poolForm.provider,
          endpoints,
          providerConfig,
          enabled: poolForm.enabled,
          draining: poolForm.draining,
        };
        if (poolForm.clearApiKey) {
          patchDto.apiKey = null;
        } else if (poolForm.apiKey.trim()) {
          patchDto.apiKey = poolForm.apiKey.trim();
        }
        await hubService.patchLlmPool(poolId, patchDto, token);

        const originals = poolFormOriginalModels;
        const originalsById = new Map(originals.map((m) => [m.id, m]));

        for (const m of originals) {
          if (!drafts.some((r) => r.id === m.id)) {
            await hubService.deleteLlmPoolModel(m.id, token);
          }
        }

        for (const row of drafts) {
          if (row.id) {
            const orig = originalsById.get(row.id);
            if (!orig) continue;
            const dto: Parameters<typeof hubService.patchLlmPoolModel>[1] = {};
            if (orig.modelKey !== row.modelKey.trim()) dto.modelKey = row.modelKey.trim();
            const dn = row.displayName.trim() || null;
            if ((orig.displayName ?? '') !== (dn ?? '')) dto.displayName = dn;
            const ek = row.endpointKey.trim() || null;
            if ((orig.endpointKey ?? null) !== ek) dto.endpointKey = ek;
            if (orig.enabled !== row.enabled) dto.enabled = row.enabled;
            if (Object.keys(dto).length > 0) {
              await hubService.patchLlmPoolModel(row.id, dto, token);
            }
          } else {
            await hubService.addLlmPoolModel(
              poolId,
              {
                modelKey: row.modelKey.trim(),
                displayName: row.displayName.trim() || null,
                endpointKey: row.endpointKey.trim() || null,
                enabled: row.enabled,
              },
              token,
            );
          }
        }
      }
      closePoolForm();
      await loadPools();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to save pool');
    } finally {
      setPoolFormSaving(false);
    }
  }, [
    poolForm,
    poolFormMode,
    poolFormPoolId,
    poolFormOriginalModels,
    getToken,
    loadPools,
    closePoolForm,
  ]);

  const handleDeletePool = useCallback(
    async (id: string, key: string) => {
      if (!window.confirm(`Delete pool "${key}" and all its models?`)) return;
      try {
        const token = getToken();
        await hubService.deleteLlmPool(id, token);
        await loadPools();
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Failed to delete pool');
      }
    },
    [getToken, loadPools],
  );

  const handleCreatePolicy = useCallback(async () => {
    if (!newPolicy.name.trim()) {
      alert('Policy name is required.');
      return;
    }
    setCreatingPolicy(true);
    try {
      const token = getToken();
      const mr = Number(newPolicy.maxRetries);
      await hubService.createLlmPoolPolicy(
        {
          name: newPolicy.name.trim(),
          system: newPolicy.system.trim() || undefined,
          botId: newPolicy.botId.trim() || undefined,
          strategy: newPolicy.strategy,
          fallbackProviders: newPolicy.fallbackProviders.trim() || undefined,
          maxRetries: Number.isFinite(mr) ? mr : 2,
          enabled: newPolicy.enabled,
        },
        token,
      );
      setShowPolicyModal(false);
      resetNewPolicy();
      await loadPolicies();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to create policy');
    } finally {
      setCreatingPolicy(false);
    }
  }, [getToken, loadPolicies, newPolicy, resetNewPolicy]);

  const runPoolTest = useCallback(async () => {
    if (!poolTestSystemPrompt.trim() || !poolTestUserPrompt.trim()) {
      setPoolTestError('System and user prompts are required.');
      return;
    }
    setPoolTestLoading(true);
    setPoolTestError(null);
    setPoolTestResult(null);
    try {
      const token = getToken();
      const t = Number(poolTestTemperature);
      const m = Number(poolTestMaxTokens);
      let tools:
        | Array<{ name: string; description?: string; inputSchema?: unknown }>
        | undefined;
      let toolCallContext: Record<string, string> | undefined;
      if (poolTestMode === 'tool') {
        try {
          const parsed = JSON.parse(poolTestToolsJson);
          if (!Array.isArray(parsed) || parsed.length === 0) {
            setPoolTestError('For tool mode, tools JSON must be a non-empty array.');
            return;
          }
          tools = parsed;
        } catch {
          setPoolTestError('Invalid tools JSON.');
          return;
        }
        try {
          const parsedCtx = JSON.parse(poolTestToolContextJson);
          if (parsedCtx && typeof parsedCtx === 'object') {
            toolCallContext = parsedCtx as Record<string, string>;
          }
        } catch {
          setPoolTestError('Invalid tool context JSON.');
          return;
        }
      }
      const result = await hubService.inferenceGenerate(
        {
          systemPrompt: poolTestSystemPrompt.trim(),
          userPrompt: poolTestUserPrompt.trim(),
          mode: poolTestMode,
          adapter: poolTestAdapter,
          transport: poolTestTransport.trim() || undefined,
          provider: poolTestProvider.trim() || undefined,
          model: poolTestModel.trim() || undefined,
          temperature: Number.isFinite(t) ? t : undefined,
          maxTokens: Number.isFinite(m) && m >= 1 ? m : undefined,
          tools,
          toolCallContext,
        },
        token,
      );
      setPoolTestResult(result);
      void loadUsageRealtime();
    } catch (e) {
      setPoolTestError(e instanceof Error ? e.message : 'Inference failed');
    } finally {
      setPoolTestLoading(false);
    }
  }, [
    getToken,
    loadUsageRealtime,
    poolTestMaxTokens,
    poolTestMode,
    poolTestAdapter,
    poolTestTransport,
    poolTestProvider,
    poolTestModel,
    poolTestToolsJson,
    poolTestToolContextJson,
    poolTestSystemPrompt,
    poolTestTemperature,
    poolTestUserPrompt,
  ]);

  const tabs = useMemo(
    () =>
      ([
        { id: 'pools' as const, label: 'Pools' },
        { id: 'policies' as const, label: 'Policies' },
        { id: 'usage' as const, label: 'Usage' },
        { id: 'test' as const, label: 'Pool test' },
      ] as const),
    [],
  );

  const totals = useMemo(() => {
    const sums = usageSummary.reduce(
      (acc, r) => {
        acc.requestCount += r.requestCount;
        acc.successCount += r.successCount;
        acc.promptTokens += r.promptTokens;
        acc.completionTokens += r.completionTokens;
        return acc;
      },
      { requestCount: 0, successCount: 0, promptTokens: 0, completionTokens: 0 },
    );
    return sums;
  }, [usageSummary]);

  const poolFormEndpointKeys = useMemo(
    () => parseEndpointKeysFromJson(poolForm.endpointsJson),
    [poolForm.endpointsJson],
  );

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">LLM Pool Control</h1>
        <p className="text-gray-600 mb-8">
          Manage <strong>credential pools</strong> and <strong>pool models</strong> (hub tables{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">llm_pools</code> /{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">llm_pool_models</code>), policies, and usage. Bot-level defaults
          remain in hub <code className="text-xs bg-gray-100 px-1 rounded">llm_models</code>. Use <strong>Pool test</strong> to call{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">POST /api/inference/generate</code> — optional provider/model filters
          match resolved pool endpoints.
        </p>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                tab === t.id
                  ? 'bg-white border border-b-0 border-gray-200 -mb-px text-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'pools' && (
          <>
            <div className="mb-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => void loadPools()}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={() => openPoolFormCreate()}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
              >
                Add pool
              </button>
            </div>
            {poolsError && (
              <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{poolsError}</div>
            )}
            {poolsLoading ? (
              <div className="text-gray-500">Loading pools…</div>
            ) : pools.length === 0 ? (
              <div className="text-sm text-gray-500 bg-white rounded-lg border border-gray-200 p-6">
                No pools yet. Use <strong>Add pool</strong> — set <span className="font-mono">endpoints</span> (e.g.{' '}
                <span className="font-mono">default</span> → OpenAI-compatible base URL) and add model rows in the same
                form. For vLLM with no model rows, use <span className="font-mono">providerConfig.model</span> only.
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">On</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Models</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pools.map((p) => (
                      <tr key={p.id}>
                        <td className="px-4 py-2 font-mono text-xs text-gray-900">{p.key}</td>
                        <td className="px-4 py-2 text-gray-800">{p.name}</td>
                        <td className="px-4 py-2 font-mono text-xs">{p.provider}</td>
                        <td className="px-4 py-2 text-gray-600">{p.enabled ? 'yes' : 'no'}</td>
                        <td className="px-4 py-2 text-gray-600">{(p.models ?? []).length}</td>
                        <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                          <button
                            type="button"
                            className="text-sm text-blue-600 hover:text-blue-800"
                            onClick={() => openPoolFormEdit(p)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-sm text-red-600 hover:text-red-800"
                            onClick={() => void handleDeletePool(p.id, p.key)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === 'policies' && (
          <>
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowPolicyModal(true)}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
              >
                Add policy
              </button>
            </div>
            {policiesError && (
              <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{policiesError}</div>
            )}

            {policiesLoading ? (
              <div className="text-gray-500">Loading policies…</div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Strategy</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fallback chain</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Max retries</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {policies.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 text-sm text-gray-500">
                          No policies configured.
                        </td>
                      </tr>
                    ) : (
                      policies.map((p) => {
                        const d = policyDrafts[p.id] ?? {
                          enabled: p.enabled,
                          strategy: p.strategy,
                          fallbackProviders: p.fallbackProviders ?? '',
                          maxRetries: p.maxRetries,
                        };

                        return (
                          <tr key={p.id}>
                            <td className="px-4 py-2 text-sm text-gray-900">{p.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              {p.system ? p.system : 'global'}
                              {p.botId ? ` · bot:${p.botId}` : ''}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              <select
                                value={d.strategy}
                                onChange={(e) => {
                                  const strategy = e.target.value;
                                  setPolicyDrafts((prev) => ({
                                    ...prev,
                                    [p.id]: { ...d, strategy },
                                  }));
                                }}
                                disabled={savingPolicyId === p.id}
                                className="rounded border border-gray-300 px-2 py-1 text-sm"
                              >
                                <option value="capability_first">capability_first</option>
                                <option value="latency">latency</option>
                                <option value="cost">cost</option>
                                <option value="weight">weight</option>
                              </select>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              <input
                                value={d.fallbackProviders}
                                onChange={(e) => {
                                  const fallbackProviders = e.target.value;
                                  setPolicyDrafts((prev) => ({
                                    ...prev,
                                    [p.id]: { ...d, fallbackProviders },
                                  }));
                                }}
                                disabled={savingPolicyId === p.id}
                                className="w-64 rounded border border-gray-300 px-2 py-1 text-sm"
                                placeholder='e.g. ["openai","vllm"] or "openai,vllm"'
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              <input
                                type="number"
                                value={d.maxRetries}
                                onChange={(e) => {
                                  const v = Number(e.target.value);
                                  if (Number.isNaN(v)) return;
                                  setPolicyDrafts((prev) => ({
                                    ...prev,
                                    [p.id]: { ...d, maxRetries: v },
                                  }));
                                }}
                                disabled={savingPolicyId === p.id}
                                className="w-24 rounded border border-gray-300 px-1.5 py-1 text-sm"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="checkbox"
                                checked={d.enabled}
                                onChange={(e) => {
                                  const enabled = e.target.checked;
                                  setPolicyDrafts((prev) => ({
                                    ...prev,
                                    [p.id]: { ...d, enabled },
                                  }));
                                }}
                                disabled={savingPolicyId === p.id}
                              />
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                type="button"
                                onClick={() =>
                                  savePolicy(p.id, {
                                    enabled: d.enabled,
                                    strategy: d.strategy,
                                    fallbackProviders: d.fallbackProviders,
                                    maxRetries: d.maxRetries,
                                  })
                                }
                                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                disabled={savingPolicyId === p.id}
                              >
                                {savingPolicyId === p.id ? 'Saving…' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === 'usage' && (
          <>
            {usageRealtimeError && (
              <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{usageRealtimeError}</div>
            )}

            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Realtime counters</h2>
              {usageRealtimeLoading ? (
                <div className="text-gray-500">Loading realtime usage…</div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">System</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bot</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Requests</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Success</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Prompt</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Completion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {usageRealtime.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-4 text-sm text-gray-500">
                            No realtime usage yet (since pool service start).
                          </td>
                        </tr>
                      ) : (
                        usageRealtime.map((r) => (
                          <tr key={`${r.system}-${r.botId}-${r.provider}`}>
                            <td className="px-4 py-2 text-sm text-gray-900">{r.system}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{r.botId}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{r.provider}</td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.requestCount.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.successCount.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.promptTokens.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.completionTokens.toLocaleString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Usage summary (DB)</h2>
              <div className="mb-6 flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">System</span>
                  <input
                    value={system}
                    onChange={(e) => setSystem(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">From</span>
                  <input
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">To</span>
                  <input
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Provider (optional)</span>
                  <input
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    placeholder="openai, xai, gemini…"
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </label>

                <button
                  type="button"
                  onClick={loadUsageSummary}
                  disabled={usageSummaryLoading}
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {usageSummaryLoading ? 'Loading…' : 'Apply'}
                </button>
              </div>

              {usageSummaryError && (
                <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{usageSummaryError}</div>
              )}

              {usageSummaryLoading && !usageSummary.length ? (
                <div className="text-gray-500">Loading summary…</div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">System</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bot</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Requests</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Success</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Prompt</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Completion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {usageSummary.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-4 text-sm text-gray-500">
                            No usage for the selected range.
                          </td>
                        </tr>
                      ) : (
                        usageSummary.map((r) => (
                          <tr key={`${r.system}-${r.botId}-${r.provider}`}>
                            <td className="px-4 py-2 text-sm text-gray-900">{r.system}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{r.botId}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{r.provider}</td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.requestCount.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.successCount.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.promptTokens.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700">
                              {r.completionTokens.toLocaleString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {usageSummary.length > 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  Totals: {totals.requestCount.toLocaleString()} requests / {totals.successCount.toLocaleString()} success · Prompt{' '}
                  {totals.promptTokens.toLocaleString()} · Completion {totals.completionTokens.toLocaleString()}
                </p>
              )}
            </section>
          </>
        )}

        {tab === 'test' && (
          <section className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 space-y-2">
              <p className="font-medium text-gray-900">Test flow (this tab → hub)</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-600">
                <li>
                  UI calls <code className="text-xs bg-gray-100 px-1 rounded">POST /api/inference/generate</code> with
                  prompts + options.
                </li>
                <li>
                  <span className="font-mono text-xs">text | structured | tool | stream</span>:{' '}
                  <span className="font-mono text-xs">InferenceController</span> →{' '}
                  <span className="font-mono text-xs">LlmPoolService</span> →{' '}
                  <span className="font-mono text-xs">DispatcherService</span> (pool endpoint selection, failover, circuit
                  breaker) → provider adapters.
                </li>
                <li>
                  <span className="font-mono text-xs">adapter=langchain</span>: same pool entrypoint, but request is routed to
                  LangChain HTTP adapter path for the selected pool endpoint.
                </li>
                <li>
                  <span className="font-mono text-xs">transport</span> (optional): overrides pool{' '}
                  <span className="font-mono">provider_config.transport</span> for this call only — production traffic
                  normally omits it. For <strong>gemini</strong>, use it with <strong>Adapter</strong>:{' '}
                  <span className="font-mono">SDK</span> → <span className="font-mono">genai-sdk</span> (no override),{' '}
                  <span className="font-mono">HTTP</span> → <span className="font-mono">genai-http</span> + transport{' '}
                  <span className="font-mono">genai-http</span>. For <strong>groq</strong>, compare SDK vs OpenAI HTTP.
                </li>
              </ol>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Mode</span>
                <select
                  value={poolTestMode}
                  onChange={(e) =>
                    setPoolTestMode(
                      e.target.value as 'text' | 'structured' | 'tool' | 'stream',
                    )
                  }
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm max-w-md"
                  disabled={poolTestLoading}
                >
                  <option value="text">text — LLM pool text mode</option>
                  <option value="structured">structured — pool JSON mode</option>
                  <option value="tool">tool — pool tool-call loop (MCP)</option>
                  <option value="stream">stream — pool streaming (aggregated response)</option>
                </select>
                <span className="text-xs text-gray-500">
                  Use pool modes to validate dispatcher + adapter paths.
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">
                    Adapter{poolTestIsGeminiProvider ? ' (Gemini: SDK vs HTTP stack)' : ''}
                  </span>
                  <select
                    value={poolTestAdapter}
                    onChange={(e) => {
                      const v = e.target.value as PoolTestAdapter;
                      setPoolTestAdapter(v);
                      if (poolTestIsGeminiProvider) {
                        if (v === 'genai-http') setPoolTestTransport('genai-http');
                        else setPoolTestTransport('');
                      }
                    }}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm max-w-md"
                    disabled={poolTestLoading}
                  >
                    {poolTestIsGeminiProvider ? (
                      <>
                        <option value="genai-sdk">SDK — @google/genai (genai-sdk)</option>
                        <option value="genai-http">HTTP — REST :generateContent (genai-http)</option>
                      </>
                    ) : (
                      <>
                        <option value="http">http — OpenAI-compatible HTTP adapter</option>
                        <option value="langchain">langchain — LangChain adapter path</option>
                      </>
                    )}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">
                    {poolTestIsGeminiProvider
                      ? 'Transport (same stack as Adapter — Gemini only)'
                      : 'Transport (pool hint override)'}
                  </span>
                  {poolTestIsGeminiProvider && poolTestGeminiStackMode != null ? (
                    <select
                      value={poolTestGeminiStackMode}
                      onChange={(e) => {
                        if (e.target.value === 'http') {
                          setPoolTestTransport('genai-http');
                          setPoolTestAdapter('genai-http');
                        } else {
                          setPoolTestTransport('');
                          setPoolTestAdapter('genai-sdk');
                        }
                      }}
                      className="rounded border border-gray-300 px-2 py-1.5 text-sm max-w-md"
                      disabled={poolTestLoading}
                    >
                      <option value="sdk">SDK — @google/genai (no transport override)</option>
                      <option value="http">HTTP — Gemini REST (override genai-http)</option>
                    </select>
                  ) : (
                    <select
                      value={poolTestTransport}
                      onChange={(e) => setPoolTestTransport(e.target.value)}
                      className="rounded border border-gray-300 px-2 py-1.5 text-sm max-w-md"
                      disabled={poolTestLoading}
                    >
                      <option value="">Pool default (from provider config)</option>
                      <option value="http">http — force OpenAI-compatible HTTP (e.g. Groq REST)</option>
                      <option value="groq-sdk">groq-sdk — Groq official SDK path</option>
                    </select>
                  )}
                  <span className="text-xs text-gray-500">
                    {poolTestIsGeminiProvider ? (
                      <>
                        Same choice as <strong>Adapter</strong> — two controls stay in sync. Production omits both
                        overrides; the pool row supplies defaults.
                      </>
                    ) : (
                      <>
                        Bots omit this; only the pool row applies unless you override here (e.g. Groq SDK vs HTTP).
                      </>
                    )}
                  </span>
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Pool provider filter (optional)</span>
                  <LlmProviderSelect
                    value={poolTestProvider}
                    onChange={setPoolTestProvider}
                    disabled={poolTestLoading}
                    allowEmpty
                    emptyLabel="Any provider"
                    allowUnknownValue
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Pool model filter (optional)</span>
                  <PoolTestModelSelect
                    value={poolTestModel}
                    onChange={setPoolTestModel}
                    disabled={poolTestLoading}
                    providerSelected={poolTestProviderSelected}
                    modelKeys={poolTestSelectableModels}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 -mt-2">
                Pick a <strong>provider</strong> to load model keys from the pools table (enabled pools only). One model
                for that provider is auto-selected. Leave model as <strong>Any model</strong> to skip the filter. With no
                provider, any enabled pool may be used (failover).
              </p>
              {poolTestMode === 'tool' && (
                <div className="grid grid-cols-1 gap-4">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500">Tools JSON (required for tool mode)</span>
                    <textarea
                      value={poolTestToolsJson}
                      onChange={(e) => setPoolTestToolsJson(e.target.value)}
                      rows={6}
                      className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                      disabled={poolTestLoading}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500">Tool context JSON (optional)</span>
                    <textarea
                      value={poolTestToolContextJson}
                      onChange={(e) => setPoolTestToolContextJson(e.target.value)}
                      rows={3}
                      className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                      disabled={poolTestLoading}
                    />
                  </label>
                </div>
              )}
              <div className="flex flex-wrap gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Temperature</span>
                  <input
                    type="number"
                    step="0.1"
                    min={0}
                    max={2}
                    value={poolTestTemperature}
                    onChange={(e) => setPoolTestTemperature(e.target.value)}
                    className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={poolTestLoading}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Max tokens</span>
                  <input
                    type="number"
                    min={1}
                    value={poolTestMaxTokens}
                    onChange={(e) => setPoolTestMaxTokens(e.target.value)}
                    className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={poolTestLoading}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 -mt-2 max-w-2xl">
                Reasoning / “thinking” models often use many tokens before the short answer. If output stops mid-thought
                (no clear result), increase <span className="font-mono">Max tokens</span>. The hub shows only{' '}
                <span className="font-mono">type: &quot;text&quot;</span> segments when the API returns split reasoning +
                answer parts.
              </p>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">System prompt</span>
                <textarea
                  value={poolTestSystemPrompt}
                  onChange={(e) => setPoolTestSystemPrompt(e.target.value)}
                  rows={3}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                  disabled={poolTestLoading}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">User prompt</span>
                <textarea
                  value={poolTestUserPrompt}
                  onChange={(e) => setPoolTestUserPrompt(e.target.value)}
                  rows={3}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                  disabled={poolTestLoading}
                />
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void runPoolTest()}
                  disabled={poolTestLoading}
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {poolTestLoading ? 'Running…' : 'Run test'}
                </button>
              </div>
              {poolTestError && (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm whitespace-pre-wrap">{poolTestError}</div>
              )}
              {poolTestResult && (
                <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
                  <div className="text-xs text-green-800 mb-2 flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      mode: <span className="font-mono">{poolTestResult.mode}</span>
                    </span>
                    {poolTestResult.execution?.adapter && (
                      <span>
                        ran as: <span className="font-mono">{poolTestResult.execution.adapter}</span>
                        {poolTestResult.execution.apiSurface ? (
                          <>
                            {' '}
                            · <span className="font-mono">{poolTestResult.execution.apiSurface}</span>
                          </>
                        ) : null}
                        {poolTestResult.execution.transportHint != null &&
                        String(poolTestResult.execution.transportHint) !== '' ? (
                          <>
                            {' '}
                            · transport{' '}
                            <span className="font-mono">{String(poolTestResult.execution.transportHint)}</span>
                          </>
                        ) : null}
                      </span>
                    )}
                    {poolTestResult.transport ? (
                      <span>
                        override sent: <span className="font-mono">{poolTestResult.transport}</span>
                        {poolTestResult.mode === 'stream' ? (
                          <span className="text-gray-600"> (stream has no execution block)</span>
                        ) : null}
                      </span>
                    ) : null}
                    {poolTestResult.provider && (
                      <span>
                        provider: <span className="font-mono">{poolTestResult.provider}</span>
                      </span>
                    )}
                    {poolTestResult.model && (
                      <span>
                        model: <span className="font-mono">{poolTestResult.model}</span>
                      </span>
                    )}
                    {poolTestResult.routeTargetId && (
                      <span>
                        route target:{' '}
                        <span className="font-mono">{poolTestResult.routeTargetId}</span>
                      </span>
                    )}
                  </div>
                  {poolTestResult.usage && (
                    <div className="text-xs text-gray-700 mb-3 font-mono flex flex-wrap gap-x-4 gap-y-1">
                      <span>
                        prompt tokens: <span className="text-gray-900">{poolTestResult.usage.promptTokens}</span>
                      </span>
                      <span>
                        completion: <span className="text-gray-900">{poolTestResult.usage.completionTokens}</span>
                      </span>
                      <span>
                        total: <span className="text-gray-900">{poolTestResult.usage.totalTokens}</span>
                      </span>
                    </div>
                  )}
                  {poolTestResult.thinking?.trim() ? (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Thinking</p>
                      <pre className="whitespace-pre-wrap text-gray-800 text-sm font-sans bg-white/70 rounded p-2 border border-green-100 max-h-64 overflow-y-auto">
                        {poolTestResult.thinking}
                      </pre>
                    </div>
                  ) : null}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Result</p>
                    {(poolTestResult.result ?? '').trim() ? (
                      <pre className="whitespace-pre-wrap text-gray-900 text-sm font-sans">
                        {(poolTestResult.result ?? '').trim()}
                      </pre>
                    ) : (poolTestResult.text ?? '').trim() ? (
                      <>
                        <p className="text-xs text-gray-600 mb-1">
                          No separate <span className="font-mono">result</span>; showing combined{' '}
                          <span className="font-mono">text</span>.
                        </p>
                        <pre className="whitespace-pre-wrap text-gray-900 text-sm font-sans">
                          {(poolTestResult.text ?? '').trim()}
                        </pre>
                      </>
                    ) : (
                      <p className="text-amber-800 text-sm">
                        Empty response. If the model only returned chain-of-thought, see Thinking above or raise max
                        tokens.
                      </p>
                    )}
                    {(poolTestResult.result ?? '').trim() &&
                      (poolTestResult.text ?? '').trim() &&
                      (poolTestResult.text ?? '').trim() !== (poolTestResult.result ?? '').trim() && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-gray-500 mb-1">Combined response (`text`, legacy)</p>
                          <pre className="whitespace-pre-wrap text-gray-800 text-xs font-sans bg-white/50 rounded p-2 border border-gray-100">
                            {(poolTestResult.text ?? '').trim()}
                          </pre>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {showPoolForm && (
          <AiConfigModal
            title={poolFormMode === 'create' ? 'Add credential pool' : 'Edit credential pool'}
            maxWidth="lg"
            onClose={() => {
              if (!poolFormSaving) closePoolForm();
            }}
          >
            <p className="text-xs text-gray-500 mb-3">
              Shared <span className="font-mono">apiKey</span> + <span className="font-mono">endpoints</span> (OpenAI-style
              base URLs). Bot defaults stay in hub <span className="font-mono">llm_models</span>; schedulable routes are{' '}
              <span className="font-mono">llm_pool_models</span> below. Endpoint keys must match keys in the endpoints JSON.
              {poolFormMode === 'edit' ? (
                <>
                  {' '}
                  Leave API key blank to keep stored; use &quot;Remove stored API key&quot; to clear.
                </>
              ) : null}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Key *</span>
                <input
                  value={poolForm.key}
                  onChange={(e) => setPoolForm((s) => ({ ...s, key: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  disabled={poolFormSaving}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Name *</span>
                <input
                  value={poolForm.name}
                  onChange={(e) => setPoolForm((s) => ({ ...s, name: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                  disabled={poolFormSaving}
                />
              </label>
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">Description</span>
                <input
                  value={poolForm.description}
                  onChange={(e) => setPoolForm((s) => ({ ...s, description: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                  disabled={poolFormSaving}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Provider *</span>
                <LlmProviderSelect
                  value={poolForm.provider}
                  onChange={(provider) => setPoolForm((s) => ({ ...s, provider }))}
                  disabled={poolFormSaving}
                  allowEmpty={poolFormMode === 'create'}
                  emptyLabel="Choose vendor…"
                  allowUnknownValue={poolFormMode === 'edit'}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">API key (optional)</span>
                <input
                  type="password"
                  value={poolForm.apiKey}
                  onChange={(e) => setPoolForm((s) => ({ ...s, apiKey: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                  autoComplete="off"
                  placeholder={poolFormMode === 'edit' ? 'Leave blank to keep unchanged' : undefined}
                  disabled={poolFormSaving}
                />
              </label>
              {poolFormMode === 'edit' ? (
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={poolForm.clearApiKey}
                    onChange={(e) => setPoolForm((s) => ({ ...s, clearApiKey: e.target.checked }))}
                    disabled={poolFormSaving}
                  />
                  Remove stored API key
                </label>
              ) : null}
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">Endpoints (JSON) *</span>
                <textarea
                  value={poolForm.endpointsJson}
                  onChange={(e) => setPoolForm((s) => ({ ...s, endpointsJson: e.target.value }))}
                  rows={4}
                  className="rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  disabled={poolFormSaving}
                />
              </label>
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">
                  Provider config (JSON) → saved as <span className="font-mono">llm_pools.provider_config</span>
                </span>
                <span className="text-xs text-gray-500">
                  Optional keys: <span className="font-mono">model</span> (vLLM default model),{' '}
                  <span className="font-mono">transport</span> — Groq: omit or non-<span className="font-mono">http</span> →
                  SDK; <span className="font-mono">&quot;http&quot;</span> → OpenAI-compatible HTTP. Gemini:{' '}
                  <span className="font-mono">genai-http</span> for REST.
                </span>
                <textarea
                  value={poolForm.providerConfigJson}
                  onChange={(e) => setPoolForm((s) => ({ ...s, providerConfigJson: e.target.value }))}
                  rows={3}
                  className="rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  placeholder='{}  or  {"transport":"http"}  or  {"model":"..."}'
                  disabled={poolFormSaving}
                />
              </label>
              <div className="flex flex-wrap items-center gap-4 pt-1 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={poolForm.enabled}
                    onChange={(e) => setPoolForm((s) => ({ ...s, enabled: e.target.checked }))}
                    disabled={poolFormSaving}
                  />
                  Pool enabled
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={poolForm.draining}
                    onChange={(e) => setPoolForm((s) => ({ ...s, draining: e.target.checked }))}
                    disabled={poolFormSaving}
                  />
                  Draining
                </label>
              </div>
            </div>

            <div className="mt-6 border-t border-gray-100 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Models under this pool</span>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:underline disabled:opacity-50"
                  onClick={() => addPoolModelRow()}
                  disabled={poolFormSaving}
                >
                  + Row
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Rows with an empty model key are ignored. Saving an edit removes models you deleted from this list.
                Routing weight and capabilities are fixed in the hub for all pool models (see node registry).
              </p>
              <div className="grid gap-3">
                {poolForm.modelRows.map((row, i) => (
                  <div key={row._key} className="rounded border border-slate-200 bg-slate-50/50 p-3 grid gap-2">
                    <div className="grid gap-2 sm:grid-cols-12 sm:items-end">
                      <input
                        className="sm:col-span-4 rounded border border-slate-300 px-2 py-1.5 text-sm"
                        placeholder="modelKey (e.g. gpt-4o)"
                        value={row.modelKey}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          setPoolForm((s) => {
                            const next = [...s.modelRows];
                            next[i] = { ...row, modelKey: v };
                            return { ...s, modelRows: next };
                          });
                        }}
                        disabled={poolFormSaving}
                      />
                      <input
                        className="sm:col-span-3 rounded border border-slate-300 px-2 py-1.5 text-sm"
                        placeholder="display name"
                        value={row.displayName}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          setPoolForm((s) => {
                            const next = [...s.modelRows];
                            next[i] = { ...row, displayName: v };
                            return { ...s, modelRows: next };
                          });
                        }}
                        disabled={poolFormSaving}
                      />
                      <div className="sm:col-span-3">
                        <PoolEndpointKeySelect
                          endpointKeys={poolFormEndpointKeys}
                          value={row.endpointKey}
                          onChange={(endpointKey) => {
                            setPoolForm((s) => {
                              const next = [...s.modelRows];
                              next[i] = { ...row, endpointKey };
                              return { ...s, modelRows: next };
                            });
                          }}
                          disabled={poolFormSaving}
                        />
                      </div>
                      <label className="sm:col-span-1 flex items-center gap-1.5 text-sm pb-1.5">
                        <input
                          type="checkbox"
                          checked={row.enabled}
                          onChange={(ev) => {
                            const enabled = ev.target.checked;
                            setPoolForm((s) => {
                              const next = [...s.modelRows];
                              next[i] = { ...row, enabled };
                              return { ...s, modelRows: next };
                            });
                          }}
                          disabled={poolFormSaving}
                        />
                        On
                      </label>
                      <div className="sm:col-span-1 flex sm:justify-end">
                        <button
                          type="button"
                          className="text-sm text-red-600 hover:underline disabled:opacity-50"
                          onClick={() => removePoolModelRow(i)}
                          disabled={poolFormSaving}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
              <button
                type="button"
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                onClick={() => closePoolForm()}
                disabled={poolFormSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => void handleSavePoolForm()}
                disabled={poolFormSaving}
              >
                {poolFormSaving ? 'Saving…' : poolFormMode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </AiConfigModal>
        )}

        {showPolicyModal && (
          <AiConfigModal
            title="Add routing policy"
            maxWidth="lg"
            onClose={() => {
              setShowPolicyModal(false);
              resetNewPolicy();
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">Name * (unique)</span>
                <input
                  value={newPolicy.name}
                  onChange={(e) => setNewPolicy((s) => ({ ...s, name: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                  placeholder="e.g. default-global"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">System (optional)</span>
                <input
                  value={newPolicy.system}
                  onChange={(e) => setNewPolicy((s) => ({ ...s, system: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                  placeholder="empty = global"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Bot ID (optional)</span>
                <input
                  value={newPolicy.botId}
                  onChange={(e) => setNewPolicy((s) => ({ ...s, botId: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Strategy</span>
                <select
                  value={newPolicy.strategy}
                  onChange={(e) => setNewPolicy((s) => ({ ...s, strategy: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                >
                  <option value="capability_first">capability_first</option>
                  <option value="latency">latency</option>
                  <option value="cost">cost</option>
                  <option value="weight">weight</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Max retries</span>
                <input
                  type="number"
                  value={newPolicy.maxRetries}
                  onChange={(e) => setNewPolicy((s) => ({ ...s, maxRetries: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">Fallback providers</span>
                <input
                  value={newPolicy.fallbackProviders}
                  onChange={(e) => setNewPolicy((s) => ({ ...s, fallbackProviders: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  placeholder='["openai","vllm"] or openai,vllm'
                />
              </label>
              <label className="flex items-center gap-2 text-sm pt-1">
                <input
                  type="checkbox"
                  checked={newPolicy.enabled}
                  onChange={(e) => setNewPolicy((s) => ({ ...s, enabled: e.target.checked }))}
                />
                Enabled
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
              <button
                type="button"
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                onClick={() => {
                  setShowPolicyModal(false);
                  resetNewPolicy();
                }}
                disabled={creatingPolicy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => void handleCreatePolicy()}
                disabled={creatingPolicy}
              >
                {creatingPolicy ? 'Creating…' : 'Create'}
              </button>
            </div>
          </AiConfigModal>
        )}
      </div>
    </div>
  );
}

